import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Target, Save, Loader2 } from "lucide-react";

const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: 6 }, (_, i) => CURRENT_YEAR - 2 + i);

const FIELDS = [
  { k: "booking_target", l: "Booking target", suf: "/10", step: "0.1" },
  { k: "pulizia_booking_target", l: "Pulizia Booking target", suf: "/10", step: "0.1" },
  { k: "google_target", l: "Google target", suf: "/5", step: "0.1" },
  { k: "staff_target", l: "Staff target", suf: "/10", step: "0.1" },
  { k: "transfer_annuale", l: "Transfer target annuo", suf: "" },
  { k: "food_annuale", l: "Food target annuo (€)", suf: "" },
  { k: "fatturato_annuale", l: "Fatturato target annuo (€)", suf: "" },
];

export default function ConfigurazioneObiettiviHotel() {
  const [hotels, setHotels] = useState([]);
  const [hotelId, setHotelId] = useState("");
  const [anno, setAnno] = useState(CURRENT_YEAR);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/hotels").then((r) => { setHotels(r.data || []); if (r.data?.[0]) setHotelId(r.data[0].id); }); }, []);

  useEffect(() => {
    if (!hotelId || !anno) return;
    setLoading(true);
    api.get(`/admin/hotel-targets/${hotelId}?anno=${anno}`).then((r) => {
      setForm(r.data || { hotel_id: hotelId, anno });
    }).finally(() => setLoading(false));
  }, [hotelId, anno]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { hotel_id: hotelId, anno };
      for (const f of FIELDS) {
        const v = form[f.k];
        if (v !== "" && v !== null && v !== undefined) payload[f.k] = f.step ? parseFloat(v) : parseInt(v);
      }
      const { data } = await api.post("/admin/hotel-targets", payload);
      setForm(data);
      toast.success("Obiettivi salvati");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-5" data-testid="obiettivi-hotel-page">
      <div>
        <h1 className="font-display tracking-tight flex items-center gap-2"><Target className="h-5 w-5 text-lavender-600" /> Obiettivi Hotel</h1>
        <p className="text-sm text-muted-foreground mt-1">Configura target annuali per hotel: valori usati come benchmark nelle relazioni mensili.</p>
      </div>

      <Card className="p-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
          <div className="space-y-1.5">
            <label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Hotel</label>
            <Select value={hotelId} onValueChange={setHotelId}>
              <SelectTrigger data-testid="hotel-select"><SelectValue /></SelectTrigger>
              <SelectContent>{hotels.map((h) => <SelectItem key={h.id} value={h.id}>{h.nome}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Anno</label>
            <Select value={String(anno)} onValueChange={(v) => setAnno(parseInt(v))}>
              <SelectTrigger data-testid="year-select"><SelectValue /></SelectTrigger>
              <SelectContent>{YEARS.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>

        {loading ? <div className="py-6 text-center"><Loader2 className="h-4 w-4 animate-spin inline" /></div> : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {FIELDS.map((f) => (
              <div key={f.k} className="space-y-1.5">
                <label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">{f.l}</label>
                <div className="relative">
                  <Input type="number" step={f.step || "1"} value={form[f.k] ?? ""} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })} data-testid={`target-${f.k}`} className="pr-12" />
                  {f.suf && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">{f.suf}</span>}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-5 flex justify-end">
          <Button onClick={save} disabled={saving || !hotelId} data-testid="save-targets" className="gap-1.5">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Salva obiettivi
          </Button>
        </div>
      </Card>

      <p className="text-xs text-muted-foreground">I target vengono usati nelle relazioni mensili per evidenziare in verde/rosso i KPI effettivi vs obiettivo (feature disponibile in R1b).</p>
    </div>
  );
}
